import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import type { FinanceChannelFieldAvailability } from '@/api/generated/model';
import { Card, CardContent } from '@/components/ui/card';
import { formatCompactMoney } from '@/lib/money';
import { cn } from '@/lib/utils';
import type { RevenueSourceView } from './RevenueBreakdownCard';
import { NotReadyInline, NotReadyNote } from './FinanceNotReady';

export interface FinanceKpiView {
  key?: string;
  label: string;
  amount: number | null;
  delta: number | null;
  description?: string;
  availability?: FinanceChannelFieldAvailability;
  note?: string;
}

export interface FinancePayoutStatusView {
  paid: number | null;
  paidAvailability?: FinanceChannelFieldAvailability;
  pending: number | null;
  pendingAvailability?: FinanceChannelFieldAvailability;
  failed: number | null;
  failedAvailability?: FinanceChannelFieldAvailability;
  onHold: number | null;
  onHoldAvailability?: FinanceChannelFieldAvailability;
}

type NestedLine = {
  label: string;
  amount: number | null;
  percentage?: number | null;
  // exactOptionalPropertyTypes: callers pass computed values that may be undefined.
  availability?: FinanceChannelFieldAvailability | undefined;
  reason?: string;
};

function byKey(metrics: FinanceKpiView[], key: string): FinanceKpiView | undefined {
  return metrics.find((metric) => metric.key === key);
}

function isLiveAmount(
  amount: number | null | undefined,
  availability?: FinanceChannelFieldAvailability,
): boolean {
  return (availability ?? 'AVAILABLE') === 'AVAILABLE' && amount != null;
}

function isPendingAvailability(availability?: FinanceChannelFieldAvailability): boolean {
  return availability === 'INCOMPLETE_FEES' || availability === 'COMING_SOON';
}

function moneyText(
  amount: number | null | undefined,
  currency: string,
  availability?: FinanceChannelFieldAvailability,
): string {
  return isLiveAmount(amount, availability)
    ? formatCompactMoney(amount!, currency)
    : '—';
}

function pctOf(part: number | null, whole: number | null): number | null {
  if (part == null || whole == null || whole <= 0) return null;
  return (part / whole) * 100;
}

function sourceByNames(
  sources: RevenueSourceView[],
  names: string[],
): RevenueSourceView | undefined {
  const wanted = new Set(names.map((name) => name.toLowerCase()));
  return sources.find((source) => {
    const keys = [source.name, source.category].filter(Boolean).map((v) => v!.toLowerCase());
    return keys.some((key) => wanted.has(key));
  });
}

function NestedRow({
  label,
  amount,
  currency,
  percentage,
  availability,
  reason,
}: NestedLine & { currency: string }) {
  const pending = isPendingAvailability(availability);

  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-[13px] leading-5">
      <span className="min-w-0 truncate text-muted-foreground">
        {label}
        {pending ? (
          <NotReadyInline
            {...(reason != null ? { reason } : {})}
          />
        ) : null}
      </span>
      <span className="flex shrink-0 items-baseline gap-3 tabular-nums">
        <span
          className={cn(
            'font-medium',
            pending || !isLiveAmount(amount, availability)
              ? 'text-muted-foreground'
              : 'text-foreground',
          )}
        >
          {moneyText(amount, currency, availability)}
        </span>
        {percentage != null ? (
          <span className="w-12 text-right text-muted-foreground">{percentage.toFixed(1)}%</span>
        ) : null}
      </span>
    </div>
  );
}

function KpiCard({
  title,
  amount,
  delta,
  currency,
  availability,
  rows,
  footnote,
}: {
  title: string;
  amount: number | null;
  delta: number | null;
  currency: string;
  availability?: FinanceChannelFieldAvailability;
  rows?: NestedLine[];
  footnote?: string;
}) {
  const live = isLiveAmount(amount, availability);
  const hasDelta = live && delta != null;
  const rising = (delta ?? 0) >= 0;
  const DeltaIcon = rising ? ArrowUpRight : ArrowDownRight;

  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardContent className="flex h-full flex-col gap-4 p-5">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <div className="flex items-end justify-between gap-3">
            <p
              className={cn(
                'text-[32px] font-bold leading-none tracking-tight tabular-nums',
                !live && 'text-muted-foreground',
              )}
            >
              {moneyText(amount, currency, availability)}
            </p>
            {hasDelta ? (
              <span
                className={cn(
                  'mb-1 inline-flex items-center gap-0.5 text-sm font-medium',
                  rising ? 'text-sky-400' : 'text-rose-400',
                )}
              >
                {rising ? '+' : ''}
                {delta!.toFixed(2)}%
                <DeltaIcon className="size-3.5" aria-hidden />
              </span>
            ) : (
              <span className="mb-1 text-sm text-muted-foreground">—</span>
            )}
          </div>
        </div>

        {rows && rows.length > 0 ? (
          <div className="mt-auto divide-y divide-border/40 border-t border-border/40 pt-1">
            {rows.map((row) => (
              <NestedRow key={row.label} currency={currency} {...row} />
            ))}
          </div>
        ) : null}

        {footnote ? (
          <NotReadyNote className="border-t border-border/40 pt-3">{footnote}</NotReadyNote>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Figma Owner strip: Gross Transaction Value · Platform Revenue · Creator Earnings.
 */
export function FinanceKpiCards({
  metrics,
  currency,
  revenueSources = [],
  payoutStatus,
}: {
  metrics: FinanceKpiView[];
  currency: string;
  /** Gross-by-source mix until platform-fee-by-source exists. */
  revenueSources?: RevenueSourceView[];
  payoutStatus?: FinancePayoutStatusView;
}) {
  const gross = byKey(metrics, 'GROSS_REVENUE');
  const platform = byKey(metrics, 'PLATFORM_REVENUE');
  const creator = byKey(metrics, 'CREATOR_PAYOUTS');
  const fees = byKey(metrics, 'PROVIDER_FEES');
  const net = byKey(metrics, 'NET_SETTLEMENT');

  const feesIncomplete =
    isPendingAvailability(fees?.availability) || isPendingAvailability(net?.availability);
  const platformFeeEmpty =
    (gross?.amount ?? 0) > 0 && (platform?.amount == null || platform.amount === 0);

  const grossRows: NestedLine[] = [
    {
      label: 'Net Settlement',
      amount: net?.amount ?? null,
      ...(net?.availability != null ? { availability: net.availability } : {}),
      reason:
        net?.note ??
        'Net Settlement waits on provider fees (and later refunds). Do not invent Gross − Creator.',
    },
    {
      label: 'Total Fees',
      amount: fees?.amount ?? null,
      ...(fees?.availability != null ? { availability: fees.availability } : {}),
      reason:
        fees?.note ??
        'Fee columns are empty for this window. Apple IAP commission is not modeled yet.',
    },
  ];

  const platformCategories: { label: string; names: string[] }[] = [
    { label: 'Subscriptions', names: ['subscriptions', 'subscribe'] },
    { label: 'Tips', names: ['tips', 'tip'] },
    { label: 'Paid Posts', names: ['paid posts', 'posts', 'exclusive'] },
  ];
  const platformRows: NestedLine[] = platformCategories.map(({ label, names }) => {
    const source = sourceByNames(revenueSources, names);
    return {
      label,
      amount: null,
      percentage: source?.percentage ?? null,
      availability: 'COMING_SOON' as const,
      reason:
        'Source amounts are not ready. The % is Gross mix until platform-fee-by-source exists.',
    };
  });

  const paid = payoutStatus?.paid ?? null;
  const pending = payoutStatus?.pending ?? null;
  const failed = payoutStatus?.failed ?? null;
  const onHold = payoutStatus?.onHold ?? null;
  const onHoldFailed =
    failed != null || onHold != null ? (failed ?? 0) + (onHold ?? 0) : null;
  const transferSum =
    (paid ?? 0) + (pending ?? 0) + (failed ?? 0) + (onHold ?? 0);
  const transferBase = transferSum > 0 ? transferSum : null;
  const onHoldPending =
    payoutStatus?.onHoldAvailability === 'COMING_SOON' && failed == null;
  const creatorRows: NestedLine[] = [
    {
      label: 'Paid to Creators',
      amount: paid,
      percentage: pctOf(paid, transferBase),
      ...(payoutStatus?.paidAvailability != null
        ? { availability: payoutStatus.paidAvailability }
        : {}),
    },
    {
      label: 'Pending Payouts',
      amount: pending,
      percentage: pctOf(pending, transferBase),
      ...(payoutStatus?.pendingAvailability != null
        ? { availability: payoutStatus.pendingAvailability }
        : {}),
    },
    {
      label: 'On Hold / Failed',
      amount: onHoldFailed,
      percentage: pctOf(onHoldFailed, transferBase),
      availability: onHoldPending
        ? 'COMING_SOON'
        : (payoutStatus?.failedAvailability ?? payoutStatus?.onHoldAvailability),
      reason:
        'On Hold is not ready — no transfer status enum yet. Failed transfers show when present.',
    },
  ];

  const platformFootnote = [
    platformFeeEmpty
      ? 'Platform fee columns are empty on payment rows — TipMi take shows $0 until writers populate them.'
      : null,
    'Source amounts not ready yet. % is Gross mix until platform-fee-by-source exists.',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <KpiCard
        title="Gross Transaction Value"
        amount={gross?.amount ?? null}
        delta={gross?.delta ?? null}
        currency={currency}
        {...(gross?.availability != null ? { availability: gross.availability } : {})}
        rows={grossRows}
        {...(feesIncomplete
          ? {
              footnote:
                'Net Settlement and Total Fees not ready — provider fee columns are empty for this window.',
            }
          : {})}
      />
      <KpiCard
        title="Platform Revenue"
        amount={platform?.amount ?? null}
        delta={platform?.delta ?? null}
        currency={currency}
        {...(platform?.availability != null ? { availability: platform.availability } : {})}
        rows={platformRows}
        footnote={platformFootnote}
      />
      <KpiCard
        title="Creator Earnings"
        amount={creator?.amount ?? null}
        delta={creator?.delta ?? null}
        currency={currency}
        {...(creator?.availability != null ? { availability: creator.availability } : {})}
        rows={creatorRows}
        {...(onHoldPending
          ? {
              footnote:
                'On Hold is not ready yet. Failed transfers show when present; On Hold awaits payout-status support.',
            }
          : {})}
      />
    </div>
  );
}
